// client/src/coms/e2ee/cryptoSession.js
// This browser's encryption device: loads matrix-sdk-crypto-wasm (vendored under /vendor, fetched only when
// encryption is used), keeps the device's keys in IndexedDB, and decides per conversation whether this device has
// really established encryption. Every "is this encrypted?" decision in the UI comes from here, so indicators follow
// the keys on this device, never a database flag.
import { createE2eeEngine } from './engine.mjs';

// index.js, not index.mjs: nginx serves .mjs with a MIME type browsers refuse for modules (see VENDOR.md).
const SDK_URL = '/vendor/matrix-sdk-crypto-wasm/18.9.0/index.js';
const EVENT = 'vox-e2ee-changed';
const SYNC_MS = 5000;
const SEEN_MAX = 3000;
const ORIGINS_MAX = 5000;
const VERIFY_EVENT = 'vox-e2ee-verify-device';

let sdkPromise = null;
let current = null; // { mx, deviceId, engine, ready: Promise }
const threads = new Map(); // threadId -> { state: 'pending' | 'ready' | 'failed', error }
const results = new Map(); // message id -> { ok, body, verified, senderDevice, error, code }
let syncTimer = null;

function emit() {
  try {
    window.dispatchEvent(new CustomEvent(EVENT));
  } catch {}
}

export function onE2eeChange(fn) {
  window.addEventListener(EVENT, fn);
  return () => window.removeEventListener(EVENT, fn);
}

function ls(key, value) {
  try {
    if (value === undefined) return window.localStorage.getItem(key);
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {}
  return null;
}

export function cryptoAvailable() {
  return typeof WebAssembly === 'object' && typeof indexedDB !== 'undefined' && !!(window.crypto && window.crypto.subtle);
}

function loadSdk() {
  if (!sdkPromise) {
    sdkPromise = import(/* webpackIgnore: true */ SDK_URL).then(async (mod) => {
      await mod.initAsync();
      return mod;
    });
    sdkPromise.catch(() => {
      sdkPromise = null;
    });
  }
  return sdkPromise;
}

function newDeviceId() {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = new Uint8Array(12);
  window.crypto.getRandomValues(bytes);
  return `VX${Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('')}`;
}

async function api(method, path, body, deviceId) {
  const res = await fetch(`/api/coms${path}`, {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(deviceId ? { 'X-Vox-E2EE-Device': deviceId } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const e = new Error((data && data.error) || `Request failed (${res.status})`);
    e.status = res.status;
    e.code = data && data.code;
    e.data = data;
    throw e;
  }
  return data;
}

function transportFor(deviceId) {
  return {
    uploadKeys: (b) => api('POST', '/e2ee/keys/upload', b, deviceId),
    queryKeys: (b) => api('POST', '/e2ee/keys/query', b, deviceId),
    claimKeys: (b) => api('POST', '/e2ee/keys/claim', b, deviceId),
    sendToDevice: (t, txn, b) => api('PUT', `/e2ee/sendToDevice/${encodeURIComponent(t)}/${encodeURIComponent(txn)}`, b, deviceId),
    sync: (since) => api('GET', `/e2ee/sync?since=${encodeURIComponent(since || '')}`, null, deviceId),
    uploadSigningKeys: (b, { reset } = {}) => api('POST', `/e2ee/keys/device_signing/upload${reset ? '?reset=1' : ''}`, b, deviceId),
    uploadSignatures: (b) => api('POST', '/e2ee/keys/signatures/upload', b, deviceId),
    createBackupVersion: (b) => api('POST', '/e2ee/room_keys/version', b, deviceId),
    backupKeys: (v, b) => api('PUT', `/e2ee/room_keys/keys?version=${encodeURIComponent(v)}`, b, deviceId),
    getBackupKeys: (v) => api('GET', `/e2ee/room_keys/keys?version=${encodeURIComponent(v)}`, null, deviceId),
    getRecovery: () => api('GET', '/e2ee/recovery', null, deviceId),
    putRecovery: (b) => api('PUT', '/e2ee/recovery', b, deviceId),
  };
}

// The device store (keys, sessions) is encrypted by the library with a random passphrase. The passphrase is kept
// in IndexedDB encrypted with a non-extractable AES-GCM key (WebCrypto), so scripts cannot export that key and the
// store is not plain JSON on disk. This does not protect against malware or someone using this browser profile.
const KEYRING_DB = 'vox-e2ee-keyring';

function idb(op) {
  return new Promise((resolve, reject) => {
    const open = indexedDB.open(KEYRING_DB, 1);
    open.onupgradeneeded = () => open.result.createObjectStore('keys');
    open.onerror = () => reject(open.error);
    open.onsuccess = () => {
      const tx = open.result.transaction('keys', 'readwrite');
      const req = op(tx.objectStore('keys'));
      tx.oncomplete = () => {
        open.result.close();
        resolve(req && req.result);
      };
      tx.onerror = () => reject(tx.error);
    };
  });
}

async function storePassphrase(storeName) {
  const subtle = window.crypto.subtle;
  const rec = await idb((s) => s.get(storeName));
  if (rec && rec.key && rec.iv && rec.wrapped) {
    const plain = await subtle.decrypt({ name: 'AES-GCM', iv: rec.iv }, rec.key, rec.wrapped);
    return new TextDecoder().decode(plain);
  }
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  const passphrase = btoa(String.fromCharCode(...bytes));
  const key = await subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const wrapped = await subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(passphrase));
  await idb((s) => s.put({ key, iv, wrapped }, storeName));
  return passphrase;
}

// Which message first carried each ciphertext (replay check), kept per device and capped. The same small
// localStorage map also holds which device delivered each room key over Olm (`.origins`): public metadata only
// (room, session and device ids), never key material.
function seenLedger(prefix, suffix = 'seen', max = SEEN_MAX) {
  const key = `${prefix}.${suffix}`;
  let map = null;
  const load = () => {
    if (map) return map;
    try {
      map = new Map(JSON.parse(ls(key) || '[]'));
    } catch {
      map = new Map();
    }
    return map;
  };
  return {
    get: (k) => load().get(k),
    set: (k, v) => {
      const m = load();
      m.set(k, v);
      while (m.size > max) m.delete(m.keys().next().value);
      ls(key, JSON.stringify(Array.from(m.entries())));
    },
  };
}

async function startDevice(sdk, mx, deviceId) {
  const prefix = `vox.e2ee.${mx}.${deviceId}`;
  const storeName = `vox-e2ee2:${mx}:${deviceId}`;
  const engine = createE2eeEngine({
    sdk,
    transport: transportFor(deviceId),
    userId: mx,
    deviceId,
    storeName,
    storePassphrase: await storePassphrase(storeName),
    tokens: { get: () => ls(`${prefix}.since`) || '', set: (v) => ls(`${prefix}.since`, String(v || '')) },
    seen: seenLedger(prefix),
    keyOrigins: seenLedger(prefix, 'origins', ORIGINS_MAX),
  });
  await engine.start();
  return engine;
}

function scheduleSync() {
  if (syncTimer) return;
  syncTimer = setInterval(() => {
    if (!current || document.visibilityState !== 'visible') return;
    current.ready.then((c) => c.engine.sync()).catch(() => {});
  }, SYNC_MS);
}

// Creates (or reopens) this browser's encryption device for the account and publishes its public keys. If the
// server no longer accepts this device (removed elsewhere, or the local keys were lost), the browser forgets it and
// reports 'device_removed'; a replacement is only set up by an explicit action (replaceRemoved: verifying this
// browser, accepting encryption, turning it on), never just because something encrypted was opened.
export function ensureDevice(mx, { replaceRemoved = false } = {}) {
  if (!mx) return Promise.reject(new Error('Unknown account'));
  if (current && current.mx === mx) return current.ready;
  if (current) current.ready.then((c) => c.engine.close()).catch(() => {});
  threads.clear();
  results.clear();
  const idKey = `vox.e2ee.device.${mx}`;
  const entry = { mx };
  entry.ready = (async () => {
    if (!cryptoAvailable()) throw Object.assign(new Error('This browser cannot run end-to-end encryption'), { code: 'unsupported' });
    const sdk = await loadSdk();
    let deviceId = ls(idKey) || newDeviceId();
    let engine;
    try {
      engine = await startDevice(sdk, mx, deviceId);
    } catch (e) {
      if (!(e && (e.code === 'device_keys_mismatch' || e.code === 'device_revoked'))) throw e;
      ls(idKey, null);
      if (!replaceRemoved) {
        throw Object.assign(new Error('This browser’s encryption device was removed. Verify this browser again to use encryption here.'), { code: 'device_removed' });
      }
      deviceId = newDeviceId();
      engine = await startDevice(sdk, mx, deviceId);
    }
    ls(idKey, deviceId);
    entry.deviceId = deviceId;
    entry.engine = engine;
    scheduleSync();
    return entry;
  })();
  entry.ready.catch(() => {
    if (current === entry) current = null;
  });
  current = entry;
  return entry.ready;
}

export function currentDeviceId() {
  return (current && current.deviceId) || '';
}

// Whether this browser already holds an encryption device for the account. Viewing an encrypted server or DM never
// creates one: a fresh device could not read anything, and devices are only set up when the user verifies this
// browser, accepts encryption or turns it on.
export function hasLocalDevice(mx) {
  return !!mx && ((current && current.mx === mx) || !!ls(`vox.e2ee.device.${mx}`));
}

// Opens the "Verify this device" dialog (DeviceVerifyHost) from wherever an encrypted message cannot be read.
export function requestDeviceVerification(mx) {
  try {
    window.dispatchEvent(new CustomEvent(VERIFY_EVENT, { detail: { mx: mx || '' } }));
  } catch {}
}

export function onDeviceVerificationRequest(fn) {
  const h = (e) => fn((e && e.detail) || {});
  window.addEventListener(VERIFY_EVENT, h);
  return () => window.removeEventListener(VERIFY_EVENT, h);
}

// This device's standing: does the account have an identity (on the server), does this device hold it, and is
// this device signed by it? Only a signed device can send or receive encrypted messages.
export async function deviceTrust(mx) {
  if (!hasLocalDevice(mx)) {
    // Nothing to open on this browser: answer from the account alone, without creating a device.
    const acct = await accountEncryptionStatus().catch(() => ({ has_identity: false }));
    return { identity: false, deviceSigned: false, serverIdentity: !!acct.has_identity, deviceId: '' };
  }
  const c = await ensureDevice(mx);
  const t = await c.engine.ownTrust();
  let serverIdentity = false;
  try {
    const r = await api('GET', '/e2ee/recovery', null, c.deviceId);
    serverIdentity = !!(r && r.has_identity);
  } catch (e) {
    serverIdentity = !!(e && e.data && e.data.has_identity);
  }
  return { ...t, serverIdentity, deviceId: c.deviceId };
}

// The account's encryption as the server describes it (public facts only), without opening a device here.
export async function accountEncryptionStatus() {
  try {
    const r = await api('GET', '/e2ee/recovery');
    return { has_identity: !!(r && r.has_identity), has_recovery: true };
  } catch (e) {
    if (e && e.status === 404) return { has_identity: !!(e.data && e.data.has_identity), has_recovery: false };
    throw e;
  }
}

function trustChanged() {
  threads.clear();
  results.clear();
  emit();
}

// First device of the account: creates the identity and returns the recovery key (shown to the user once).
export async function setupIdentity(mx, { reset = false } = {}) {
  const c = await ensureDevice(mx, { replaceRemoved: true });
  const key = await c.engine.bootstrap({ reset });
  trustChanged();
  return key;
}

// A further device: verifies it with the recovery key and restores message history from the key backup.
export async function restoreWithRecoveryKey(mx, recoveryKey) {
  const c = await ensureDevice(mx, { replaceRemoved: true });
  const r = await c.engine.restore(recoveryKey);
  trustChanged();
  return r;
}

export async function contactIdentity(myMx, otherMx) {
  const c = await ensureDevice(myMx);
  const [mine, theirs] = await Promise.all([c.engine.identity(myMx), c.engine.identity(otherMx)]);
  return { mine, theirs };
}

export async function verifyContact(myMx, otherMx) {
  const c = await ensureDevice(myMx);
  const r = await c.engine.verifyUser(otherMx);
  trustChanged();
  return r;
}

export async function acceptContactChange(myMx, otherMx) {
  const c = await ensureDevice(myMx);
  const r = await c.engine.acceptIdentityChange(otherMx);
  trustChanged();
  return r;
}

export function threadSession(threadId) {
  return threads.get(String(threadId)) || { state: 'none', error: '' };
}

export function hasEstablishedDmSession(threadId) {
  return threadSession(threadId).state === 'ready';
}

// Sets up encryption for a conversation on this device: fetches the participants' device keys, opens Olm sessions
// and shares this device's room key. Only after this succeeds does the conversation show a lock here.
export async function prepareThread(threadId, e2ee) {
  const id = String(threadId);
  const prev = threads.get(id);
  // A failed setup is only retried on request (retryThread), never in a loop.
  if (prev) return prev.promise;
  const rec = { state: 'pending', error: '' };
  const mx = e2ee.self || e2ee.members[0];
  if (!hasLocalDevice(mx)) {
    // Viewing never creates a device: this browser has to be verified first (DeviceVerifyHost).
    Object.assign(rec, { state: 'failed', code: 'device_unverified', error: 'This browser is not verified', promise: Promise.resolve() });
    threads.set(id, rec);
    emit();
    return rec.promise;
  }
  rec.promise = (async () => {
    try {
      const c = await ensureDevice(mx);
      await c.engine.prepare(e2ee.room_id, e2ee.members);
      rec.state = 'ready';
    } catch (e) {
      rec.state = 'failed';
      rec.error = String((e && e.message) || e);
      rec.code = (e && e.code) || '';
      rec.user = (e && e.user) || '';
    }
    emit();
  })();
  threads.set(id, rec);
  emit();
  return rec.promise;
}

export function retryThread(threadId, e2ee) {
  threads.delete(String(threadId));
  return prepareThread(threadId, e2ee);
}

// Encrypts on this device and posts only the ciphertext. Throws (and sends nothing) if encryption fails.
export async function sendEncryptedDm(threadId, e2ee, text) {
  const c = await ensureDevice(e2ee.members[0]);
  const ciphertext = await c.engine.encrypt(e2ee.room_id, e2ee.members, text);
  const res = await api('POST', `/dms/thread/${encodeURIComponent(threadId)}/messages`, { encryption: 'e2ee', ciphertext }, c.deviceId);
  const msg = res && res.message;
  if (msg && msg.id) {
    // Our own Megolm messages decrypt here too, but remembering the text avoids a round trip.
    results.set(String(msg.id), { ok: true, body: String(text), verified: false, senderDevice: c.deviceId });
  }
  return res;
}

export function decryptedResult(messageId) {
  return results.get(String(messageId)) || null;
}

// Server channels: encrypts on this device for the channel's current key members. The caller posts the result.
// Throws (and nothing is sent) if encryption fails.
export async function encryptForChannel(channelId, info, text) {
  const c = await ensureDevice(info.self);
  const ciphertext = await c.engine.encrypt(info.room_id, info.members, text);
  return { ciphertext, deviceId: c.deviceId };
}

export function rememberChannelPlaintext(messageId, text, deviceId) {
  results.set(`ch:${messageId}`, { ok: true, body: String(text), verified: false, senderDevice: deviceId || '' });
}

// Decrypts the encrypted messages of a conversation that this device has not handled yet. Resolves true if
// anything changed.
// Without allowNewDevice (the conversation is no longer encrypted) no device is created just to try: a new device
// could not read earlier messages anyway.
// DMs key results by message id and use event ids $vxdm<id>; server channels pass { keyPrefix: 'ch:',
// eventPrefix: '$vxch', senderField: 'user_id' }.
export async function decryptMessages(e2eeOrRoom, messages, senderMx, { allowNewDevice = false, keyPrefix = '', eventPrefix = '$vxdm', senderField = 'sender_user_id' } = {}) {
  const key = (m) => `${keyPrefix}${m.id}`;
  // New messages, plus history restored from the key backup: it is checked again, because the sender's device may
  // since have shared that session with this device directly, which confirms who sent it.
  const todo = (messages || []).filter((m) => {
    if (!m || m.encryption !== 'e2ee' || !m.ciphertext) return false;
    const prev = results.get(key(m));
    return !prev || (prev.ok && prev.fromBackup);
  });
  if (!todo.length) return false;
  const roomId = e2eeOrRoom.room_id;
  const mx = e2eeOrRoom.self || e2eeOrRoom.members[0];
  if (!allowNewDevice && !hasLocalDevice(mx)) {
    for (const m of todo) results.set(key(m), { ok: false, error: 'No encryption device on this browser', code: 'no_device' });
    return true;
  }
  let c;
  try {
    c = await ensureDevice(mx);
  } catch (e) {
    for (const m of todo) results.set(key(m), { ok: false, error: String((e && e.message) || e), code: (e && e.code) || 'unavailable' });
    return true;
  }
  // One sync for the whole page (queued room keys), then no per-message retries.
  await c.engine.sync().catch(() => {});
  let changed = false;
  for (const m of todo) {
    const prev = results.get(key(m));
    try {
      const d = await c.engine.decrypt(roomId, { ciphertext: m.ciphertext, sender: senderMx(m[senderField]), eventId: `${eventPrefix}${m.id}`, ts: Date.parse(m.created_at) || 0 }, { syncOnMiss: false });
      if (!prev || prev.fromBackup !== d.fromBackup) changed = true;
      results.set(key(m), { ok: true, ...d });
    } catch (e) {
      if (prev && prev.ok) continue; // a restored message stays readable even if a re-check fails
      changed = true;
      results.set(key(m), { ok: false, error: String((e && e.message) || e), code: (e && e.code) || 'undecryptable' });
    }
  }
  return changed;
}

// Forget a failed result so the next load tries again (e.g. after the room key has arrived).
export function forgetFailedResults() {
  for (const [k, v] of results) if (!v.ok && v.code !== 'sender_mismatch' && v.code !== 'replay') results.delete(k);
}

export function listDevices() {
  return api('GET', '/e2ee/devices');
}

export function removeDevice(deviceId) {
  return api('DELETE', `/e2ee/devices/${encodeURIComponent(deviceId)}`);
}
